<script lang="ts">
	//Svelte
	import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
	import { location } from 'svelte-spa-router';
	//Services
	import { format } from 'svelte-i18n';
	// Stores
	import { profile_data } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	// Helpers
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';
	import { isEmailUnverified } from 'src/helpers/email_verification_helpers';
	import {
		appendMissingMenuOrderIndices,
		menuItemCameraMonitoring,
		menuItemsNewLayout,
		menuItemsNewLayoutMore,
		MORE_MENU_ICON,
		parseHomeDataMenuOrderJson,
		type MenuItemDef as MenuItemType,
	} from 'src/helpers/main_menu';
	import ChatWindow from 'src/components/AISceneChatbot/ChatWindow.svelte';
	import { MenuItem, MenuItemList } from '@Shelly/shelly-design-system';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	const dispatch = createEventDispatcher<{
		'menu-item-click': { menuItem: MenuItemType };
		'logo-click': void;
	}>();

	export let activeMenuItem: MenuItemType;
	export let menuItems = [...menuItemsNewLayout, ...menuItemsNewLayoutMore];
	export let visualActiveMenuId: string | null = null;

	let windowInnerWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
	let navShellRef: HTMLElement;
	let mobileNavBarRef: HTMLDivElement;
	let navIndicatorRef: HTMLDivElement;
	let navLayerRef: HTMLDivElement;
	let mobileNavItemsRefs: HTMLElement[] = [];
	let mobileIndicatorX = 0;
	let mobileActiveColor = '102, 155, 241';
	let activeRenderIndex = -1;
	let mobileIndicatorVisible = false;
	let assistantReturnMenuId: string | null = null;
	let isNavCompact = false;
	let activeModalKey: string | null = null;
	let isModalClosing = false;
	let closeNavModalTimer: ReturnType<typeof setTimeout> | null = null;
	const navColorPalette = [
		'102, 155, 241',
		'255, 138, 91',
		'247, 201, 72',
		'88, 208, 160',
		'192, 132, 252',
		'255, 209, 102',
		'142, 194, 255',
		'255, 163, 213',
	];
	const NAV_EDGE_INSET = 5;
	const NAV_EXPAND_ANIMATION_MS = 400;
	const LOGO_MENU_ITEM_ID = 'shelly-logo';
	const MORE_MENU_ITEM_ID = 'more-menu-item';
	const ASSISTANT_MENU_ITEM_ID = 'assistant';
	const DESKTOP_NAV_BREAKPOINT = SystemScreenBreakpoints.DESKTOP_MIN;
	const logoMenuItem: MenuItemType = {
		id: LOGO_MENU_ITEM_ID,
		href: '',
		icon: '',
		label: 'button_go_to_shelly',
		labelDefault: 'Go to Shelly',
	};
	const moreMenuItem: MenuItemType = {
		id: MORE_MENU_ITEM_ID,
		href: '',
		icon: MORE_MENU_ICON,
		label: 'title_more',
		labelDefault: 'More',
	};
	let showMoreDropdown = false;
	let isMoreBtnFocused = false;
	const SCROLL_COMPACT_THRESHOLD = 24;
	const NAV_COMPACT_DISTANCE = 18;
	let lastScrollTop = 0;
	let navDownwardScrollDistance = 0;

	const clearCloseNavModalTimer = () => {
		if (!closeNavModalTimer) return;
		clearTimeout(closeNavModalTimer);
		closeNavModalTimer = null;
	};

	const syncFrozenNavBarWidth = () => {
		if (!navShellRef || !mobileNavBarRef) return;
		const currentWidth = mobileNavBarRef.offsetWidth;
		if (!currentWidth) return;
		navShellRef.style.setProperty('--nav-bar-frozen-width', `${currentWidth}px`);
	};

	const getSolidIcon = (icon: string) => {
		const [weight, name] = icon.split(' ');
		if (!name) return icon;
		if (weight === 'fak') return icon;
		return `fas ${name}`;
	};

	const handlerClickMenuItem = (menuItem: MenuItemType) => {
		clearCloseNavModalTimer();
		isModalClosing = false;
		activeModalKey = null;
		activeMenuItem = menuItem;
		dispatch('menu-item-click', { menuItem });
	};

	const handleLogoClick = () => {
		showMoreDropdown = false;
		dispatch('logo-click');
	};

	const isMoreSubItemActive = (subItem: MenuItemType, loc: string) => {
		const matches = moreMenuItems.filter((item) => item.href && loc.startsWith(item.href));
		if (!matches.length) return false;
		const best = matches.reduce((a, b) => ((b.href?.length ?? 0) > (a.href?.length ?? 0) ? b : a));
		return best.id === subItem.id;
	};

	const handleClickMoreBtn = () => {
		isMoreBtnFocused = !showMoreDropdown;
		showMoreDropdown = !showMoreDropdown;
	};

	const handleCloseMoreBtn = () => {
		showMoreDropdown = false;
		isMoreBtnFocused = false;
	};

	const handleClickMoreSubItem = (subMenuItem: MenuItemType) => {
		handleCloseMoreBtn();
		handlerClickMenuItem(subMenuItem);
	};

	const isMobileViewport = () => {
		const width =
			windowInnerWidth ||
			(typeof window !== 'undefined' ? window.innerWidth : DESKTOP_NAV_BREAKPOINT);
		return width < DESKTOP_NAV_BREAKPOINT;
	};

	const closeNavModal = () => {
		if (!activeModalKey || isModalClosing) return;
		isNavCompact = false;
		isModalClosing = true;
		clearCloseNavModalTimer();
		closeNavModalTimer = setTimeout(() => {
			activeModalKey = null;
			isModalClosing = false;
			closeNavModalTimer = null;
		}, NAV_EXPAND_ANIMATION_MS);
	};

	const openNavModal = (menuKey: string) => {
		if (!isMobileViewport()) return;
		if (!activeModalKey && !isModalClosing) {
			syncFrozenNavBarWidth();
		}
		clearCloseNavModalTimer();
		isModalClosing = false;
		activeModalKey = menuKey;
		isNavCompact = false;
	};

	const isExpandableMenuItem = (menuItemId?: string) => {
		return menuItemId === ASSISTANT_MENU_ITEM_ID;
	};

	const handleAssistantOpenRequest = () => {
		if (activeModalKey === ASSISTANT_MENU_ITEM_ID) {
			closeNavModal();
			return;
		}
		assistantReturnMenuId =
			navItemsForRender.find(
				(item) =>
					item.id !== ASSISTANT_MENU_ITEM_ID &&
					item.id !== LOGO_MENU_ITEM_ID &&
					isMenuItemActive(item),
			)?.id ??
			activeMenuItem?.id ??
			null;
		openNavModal(ASSISTANT_MENU_ITEM_ID);
	};

	const handleClickNavItem = (menuItem: MenuItemType) => {
		if (isMobileViewport() && isExpandableMenuItem(menuItem.id)) {
			if (menuItem.id === ASSISTANT_MENU_ITEM_ID) {
				handleAssistantOpenRequest();
			}
			return;
		}
		handlerClickMenuItem(menuItem);
	};

	const handleNavBarClickCapture = (event: MouseEvent) => {
		if (!isNavCompact || activeModalKey) return;
		event.preventDefault();
		event.stopPropagation();
		isNavCompact = false;
		navDownwardScrollDistance = 0;
	};

	const getItemKey = (menuItem: MenuItemType, index: number) => {
		return menuItem.id || menuItem.href || `${menuItem.label}-${index}`;
	};

	const updateMobileIndicator = async () => {
		if (!mobileNavBarRef || !navIndicatorRef || !navShellRef || !navItemsForRender.length) return;
		await tick();

		const navBarWidth = mobileNavBarRef.offsetWidth;
		const indicatorWidth = navIndicatorRef.offsetWidth;
		if (!activeModalKey && !isModalClosing) {
			navShellRef.style.setProperty('--nav-bar-frozen-width', `${navBarWidth}px`);
		}
		const indicatorPositions = mobileNavItemsRefs.map((element) => {
			if (!element) return 0;
			const localCenter = element.offsetLeft + element.offsetWidth / 2;
			const unclampedIndicatorX = localCenter - indicatorWidth / 2 - NAV_EDGE_INSET;
			const maxIndicatorX = navBarWidth - NAV_EDGE_INSET * 2 - indicatorWidth;
			return Math.max(0, Math.min(unclampedIndicatorX, maxIndicatorX));
		});

		if (!indicatorPositions.length || !navBarWidth || !indicatorWidth) {
			mobileIndicatorX = 0;
			return;
		}

		const indicatorModalTarget = isModalClosing ? assistantReturnMenuId : activeModalKey;
		const modalIndex = indicatorModalTarget
			? navItemsForRender.findIndex((item) => item.id === indicatorModalTarget)
			: -1;
		const activeIndex =
			modalIndex > -1 ? modalIndex : navItemsForRender.findIndex((item) => isMenuItemActive(item));
		const activeItem = activeIndex >= 0 ? navItemsForRender[activeIndex] : undefined;
		const hasSelectableDestination = !!activeItem && activeItem.id !== LOGO_MENU_ITEM_ID;

		activeRenderIndex = hasSelectableDestination ? activeIndex : -1;
		mobileIndicatorVisible = hasSelectableDestination;
		if (!hasSelectableDestination) return;

		mobileIndicatorX = indicatorPositions[activeIndex] ?? 0;
		mobileActiveColor = navColorPalette[activeIndex % navColorPalette.length];
		navShellRef.style.setProperty('--nav-indicator-x', `${mobileIndicatorX}px`);
		navShellRef.style.setProperty('--active-nav-rgb', mobileActiveColor);
	};

	const handleCompactNavOnScroll = (scrollTop: number) => {
		if (!isMobileViewport()) {
			isNavCompact = false;
			navDownwardScrollDistance = 0;
			lastScrollTop = Math.max(0, scrollTop);
			return;
		}

		if (activeModalKey) {
			isNavCompact = false;
			return;
		}

		const normalizedScrollTop = Math.max(0, scrollTop);
		const scrollDelta = normalizedScrollTop - lastScrollTop;

		if (scrollDelta > 0) {
			navDownwardScrollDistance += scrollDelta;
		} else {
			navDownwardScrollDistance = 0;
		}

		isNavCompact =
			normalizedScrollTop > SCROLL_COMPACT_THRESHOLD &&
			navDownwardScrollDistance > NAV_COMPACT_DISTANCE;

		lastScrollTop = normalizedScrollTop;
	};

	const handleDocumentScroll = (event: Event) => {
		const target = event.target;
		if (!(target instanceof HTMLElement)) return;
		if (!target.closest('.scroll-content-new-layout, .main-layout-scroll')) return;
		handleCompactNavOnScroll(target.scrollTop);
	};

	const handleWindowScroll = () => {
		const scrollTop =
			window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
		handleCompactNavOnScroll(scrollTop);
	};

	const isMenuItemActive = (menuItem: MenuItemType) => {
		const resolvedActiveId = visualActiveMenuId || activeMenuItem?.id || null;
		return menuItem.id === resolvedActiveId;
	};

	$: menuOrder = appendMissingMenuOrderIndices(
		parseHomeDataMenuOrderJson($profile_data?.menuOrder),
		menuItemsNewLayout,
	);

	$: availableMenuItemIds = new Set(menuItems.map((menuItem) => menuItem.id));
	$: menuItemsMainOrdered = menuOrder
		.map((position) => menuItemsNewLayout[position])
		.filter(
			(menuItem): menuItem is MenuItemType => !!menuItem && availableMenuItemIds.has(menuItem.id),
		);
	$: moreMenuItems = hasCameraDevices($devices)
		? [...menuItemsNewLayoutMore, menuItemCameraMonitoring]
		: menuItemsNewLayoutMore;
	$: isMoreBtnActive =
		visualActiveMenuId === MORE_MENU_ITEM_ID ||
		moreMenuItems.some((item) => item.id === activeMenuItem?.id);
	$: isSettingsInMore = moreMenuItems.some((item) => item.href === '/settings');
	$: navItemsForRender = (() => {
		const items = [
			...(windowInnerWidth >= DESKTOP_NAV_BREAKPOINT ? [logoMenuItem] : []),
			...menuItemsMainOrdered,
		];

		const seen = new Set<string>();
		const result: MenuItemType[] = [];
		items.forEach((item, index) => {
			const key = getItemKey(item, index);
			if (seen.has(key)) return;
			seen.add(key);
			result.push(item);
		});

		result.push(moreMenuItem);
		return result;
	})();

	$: {
		navItemsForRender;
		activeMenuItem;
		visualActiveMenuId;
		activeModalKey;
		isModalClosing;
		windowInnerWidth;
		void updateMobileIndicator();
	}

	$: if (activeModalKey && !isMobileViewport()) {
		clearCloseNavModalTimer();
		isModalClosing = false;
		activeModalKey = null;
	}

	$: {
		if (typeof document !== 'undefined') {
			document.body.classList.toggle('is-fancy-nav-modal-open', !!activeModalKey);
		}
	}

	onMount(() => {
		if (navLayerRef && navLayerRef.parentElement !== document.body) {
			document.body.appendChild(navLayerRef);
		}
		document.addEventListener('scroll', handleDocumentScroll, { capture: true, passive: true });
		window.addEventListener('scroll', handleWindowScroll, { passive: true });
	});

	onDestroy(() => {
		clearCloseNavModalTimer();
		document.removeEventListener('scroll', handleDocumentScroll, { capture: true });
		window.removeEventListener('scroll', handleWindowScroll);
		if (navLayerRef?.parentElement === document.body) {
			navLayerRef.remove();
		}
		if (typeof document !== 'undefined') {
			document.body.classList.remove('is-fancy-nav-modal-open');
		}
	});
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<div class="fancy-nav-layer" bind:this={navLayerRef}>
	{#if activeModalKey}
		<button
			type="button"
			class={`fancy-nav-backdrop ${isModalClosing ? 'is-closing' : 'is-opening'}`}
			aria-label={$format('button_close', { default: 'Close' })}
			on:click={closeNavModal}
		></button>
	{/if}

	<nav
		class={`fancy-mobile-nav ${isNavCompact ? 'is-nav-compact' : ''} ${activeModalKey ? 'is-modal-active' : ''} ${activeModalKey && !isModalClosing ? 'is-modal-open' : ''} ${isModalClosing ? 'is-modal-closing' : ''}`}
		aria-label="Primary navigation"
		bind:this={navShellRef}
	>
		<section
			class={`fancy-nav-modal-panel ${activeModalKey === ASSISTANT_MENU_ITEM_ID ? 'is-assistant' : ''}`}
			aria-hidden={!activeModalKey}
		>
			{#if activeModalKey === ASSISTANT_MENU_ITEM_ID}
				<button
					class="fancy-nav-assistant-close"
					type="button"
					aria-label={$format('button_close', { default: 'Close' })}
					on:click={closeNavModal}
				>
					×
				</button>
				<div class="fancy-nav-assistant-content">
					<ChatWindow surfaceOpen={activeModalKey === ASSISTANT_MENU_ITEM_ID} />
				</div>
			{/if}
		</section>

		<div
			class="fancy-nav-bar"
			bind:this={mobileNavBarRef}
			on:click|capture={handleNavBarClickCapture}
		>
			<div
				class="fancy-nav-indicator"
				class:is-visible={mobileIndicatorVisible}
				aria-hidden="true"
				bind:this={navIndicatorRef}
			></div>
			{#each navItemsForRender as menuItem, index (getItemKey(menuItem, index))}
				{@const isActive = index === activeRenderIndex}
				{#if menuItem.id === LOGO_MENU_ITEM_ID}
					<button
						class="fancy-nav-item fancy-nav-item-logo"
						type="button"
						aria-label={$format(menuItem.label, { default: menuItem.labelDefault })}
						style={`--item-color-rgb:${navColorPalette[index % navColorPalette.length]};--item-active-opacity:0;--item-active-scale:0;`}
						bind:this={mobileNavItemsRefs[index]}
						on:click={handleLogoClick}
					>
						<img src="/images/shelly-logo-circle.svg" alt="" class="fancy-nav-logo" />
					</button>
				{:else if menuItem.id === MORE_MENU_ITEM_ID}
					<div
						class={`fancy-nav-item fancy-nav-item-more ${isMoreBtnActive ? 'is-active' : ''}`}
						style={`--item-color-rgb:${navColorPalette[index % navColorPalette.length]};--item-active-opacity:${isMoreBtnActive ? 0.44 : 0};--item-active-scale:${isMoreBtnActive ? 1.02 : 0};`}
						bind:this={mobileNavItemsRefs[index]}
					>
						<MenuItem
							useNewLayout
							bind:showDropdown={showMoreDropdown}
							dropdownOptions={{
								positionHorizontally: !isMobileViewport(),
								closeDropdownHoverOut: false,
							}}
							active={isMoreBtnActive}
							isFocused={isMoreBtnFocused}
							icon={isMoreBtnActive ? getSolidIcon(MORE_MENU_ICON) : MORE_MENU_ICON}
							label={$format('title_more', { default: 'More' })}
							notification={isSettingsInMore && $isEmailUnverified ? 'danger' : undefined}
							on:click={handleClickMoreBtn}
							on:close={handleCloseMoreBtn}
						>
							<svelte:fragment slot="dropdown-menu-items">
								<MenuItemList
									isGroupTitle
									title={$format('title_more', { default: 'More' })}
								/>
								{#each moreMenuItems as subMenuItem}
									<MenuItemList
										isGroupItem
										icon={subMenuItem.icon}
										title={$format(subMenuItem.label, {
											default: subMenuItem.labelDefault,
										})}
										active={isMoreSubItemActive(subMenuItem, $location ?? '')}
										on:click={() => handleClickMoreSubItem(subMenuItem)}
									/>
								{/each}
							</svelte:fragment>
						</MenuItem>
					</div>
				{:else}
					<button
						class={`fancy-nav-item ${isActive ? 'is-active' : ''}`}
						type="button"
						aria-label={$format(menuItem.label, { default: menuItem.labelDefault })}
						style={`--item-color-rgb:${navColorPalette[index % navColorPalette.length]};--item-active-opacity:${isActive ? 0.44 : 0};--item-active-scale:${isActive ? 1.02 : 0};`}
						bind:this={mobileNavItemsRefs[index]}
						on:click={() => {
							handleClickNavItem(menuItem);
						}}
					>
						<i
							class={`nav-icon menu-item-icon ${getSolidIcon(menuItem.icon)}`}
							style={`color:${isActive ? `rgb(${navColorPalette[index % navColorPalette.length]})` : 'rgb(255 255 255 / 70%)'};`}
							aria-hidden="true"
						></i>
					</button>
				{/if}
				{#if menuItem.id === 'eventLog' && navItemsForRender[index + 1]?.id === 'assistant'}
					<span class="fancy-nav-divider" aria-hidden="true"></span>
				{/if}
			{/each}
		</div>
	</nav>
</div>

<style>
	.fancy-nav-layer {
		position: relative;
		z-index: 20;
	}

	:global(body.is-fancy-nav-modal-open) {
		overflow: hidden;
		overscroll-behavior: none;
	}

	.fancy-nav-backdrop {
		position: fixed;
		inset: 0;
		z-index: 20;
		isolation: isolate;
		border: 0;
		margin: 0;
		padding: 0;
		cursor: default;
		background: radial-gradient(
			120% 85% at 50% 0%,
			rgba(88, 133, 187, 0.22) 0%,
			rgba(7, 18, 38, 0.62) 52%,
			rgba(7, 18, 38, 0.78) 100%
		);
		backdrop-filter: blur(0px) saturate(108%);
		-webkit-backdrop-filter: blur(0px) saturate(108%);
		opacity: 0;
		transform: translateZ(0);
		will-change:
			opacity,
			backdrop-filter,
			-webkit-backdrop-filter;
		animation: fancy-nav-backdrop-open 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
	}

	.fancy-nav-backdrop.is-opening {
		animation: fancy-nav-backdrop-open 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
	}

	.fancy-nav-backdrop.is-closing {
		animation: fancy-nav-backdrop-close 400ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
	}

	@keyframes fancy-nav-backdrop-open {
		from {
			opacity: 0;
			background: radial-gradient(
				120% 85% at 50% 0%,
				rgba(88, 133, 187, 0.08) 0%,
				rgba(7, 18, 38, 0.18) 52%,
				rgba(7, 18, 38, 0.24) 100%
			);
			backdrop-filter: blur(0px) saturate(108%);
			-webkit-backdrop-filter: blur(0px) saturate(108%);
		}
		to {
			opacity: 1;
			background: radial-gradient(
				120% 85% at 50% 0%,
				rgba(88, 133, 187, 0.22) 0%,
				rgba(7, 18, 38, 0.62) 52%,
				rgba(7, 18, 38, 0.78) 100%
			);
			backdrop-filter: blur(6px) saturate(120%);
			-webkit-backdrop-filter: blur(6px) saturate(120%);
		}
	}

	@keyframes fancy-nav-backdrop-close {
		from {
			opacity: 1;
			background: radial-gradient(
				120% 85% at 50% 0%,
				rgba(88, 133, 187, 0.22) 0%,
				rgba(7, 18, 38, 0.62) 52%,
				rgba(7, 18, 38, 0.78) 100%
			);
			backdrop-filter: blur(6px) saturate(120%);
			-webkit-backdrop-filter: blur(6px) saturate(120%);
		}
		to {
			opacity: 0;
			background: radial-gradient(
				120% 85% at 50% 0%,
				rgba(88, 133, 187, 0.08) 0%,
				rgba(7, 18, 38, 0.18) 52%,
				rgba(7, 18, 38, 0.24) 100%
			);
			backdrop-filter: blur(0px) saturate(108%);
			-webkit-backdrop-filter: blur(0px) saturate(108%);
		}
	}

	@media (max-width: 768px) {
		.fancy-nav-backdrop {
			top: var(--ios-visual-viewport-offset-top, 0px);
			right: 0;
			left: 0;
			bottom: auto;
			height: var(--ios-visual-viewport-height, 100%);
		}
	}

	.fancy-mobile-nav {
		--nav-indicator-width: 4.125rem;
		--nav-indicator-x: 0px;
		--active-nav-rgb: 102, 155, 241;
		--nav-icon-size: 20px;
		--nav-collapsed-left: 50%;
		--nav-collapsed-width: min(84%, 720px);
		--nav-collapsed-bottom: 4%;
		--nav-collapsed-radius: 30px;
		--nav-bar-frozen-width: var(--nav-collapsed-width);
		--nav-side-padding: calc(5px + var(--nav-indicator-width) / 2 - var(--nav-icon-size) / 2);
		--nav-bar-height: 60px;
		--nav-expand-step: 20px;
		--nav-expanded-radius: calc(var(--nav-collapsed-radius) + 20px);
		--nav-open-top: calc(
			var(--ios-visual-viewport-offset-top, env(safe-area-inset-top, 0px)) + 70px
		);
		--nav-open-bottom: calc(var(--nav-collapsed-bottom) - var(--nav-expand-step));
		--nav-stage-one-height: calc(var(--nav-bar-height) + var(--nav-expand-step) * 2);
		--nav-expanded-height: calc(
			var(--ios-visual-viewport-height, 100dvh) - var(--nav-open-top) - var(--nav-open-bottom)
		);
		--nav-final-height: max(var(--nav-stage-one-height), var(--nav-expanded-height));
		position: fixed;
		left: var(--nav-collapsed-left);
		right: auto;
		bottom: var(--nav-collapsed-bottom);
		z-index: 21;
		width: var(--nav-collapsed-width);
		max-width: var(--nav-collapsed-width);
		height: var(--nav-bar-height);
		padding: 0;
		margin: 0;
		border-radius: var(--nav-collapsed-radius);
		background: rgb(0 0 0 / 0%);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		box-shadow: 0 5px 9px rgb(0 0 0 / 45%);
		overflow: hidden;
		pointer-events: none;
		transform: translateX(-50%) scale(1);
		transform-origin: center bottom;
		transition:
			transform 220ms ease,
			box-shadow 300ms ease;
	}

	.fancy-mobile-nav.is-nav-compact:not(.is-modal-active) {
		transform: translateX(-50%) scale(0.7);
	}

	.fancy-mobile-nav:not(.is-modal-active) {
		background: rgb(0 0 0 / 0%);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		box-shadow: 0 5px 9px rgb(0 0 0 / 45%);
	}

	.fancy-mobile-nav.is-modal-active {
		left: var(--nav-collapsed-left);
		width: calc(var(--nav-collapsed-width) + var(--nav-expand-step) * 2);
		max-width: calc(var(--nav-collapsed-width) + var(--nav-expand-step) * 2);
		bottom: var(--nav-open-bottom);
		height: var(--nav-final-height);
		border-radius: var(--nav-expanded-radius);
		overflow: hidden;
		pointer-events: auto;
		background: rgb(0 0 0 / 86%);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		box-shadow:
			0 12px 32px rgb(0 0 0 / 48%),
			inset 0 1px 0 rgb(255 255 255 / 10%);
	}

	.fancy-mobile-nav.is-modal-open {
		animation: fancy-nav-expand-shell 400ms cubic-bezier(0.22, 1, 0.36, 1) both;
	}

	.fancy-mobile-nav.is-modal-closing {
		animation: fancy-nav-collapse-shell 400ms cubic-bezier(0.22, 1, 0.36, 1) both;
	}

	@keyframes fancy-nav-expand-shell {
		0% {
			left: var(--nav-collapsed-left);
			width: var(--nav-collapsed-width);
			bottom: var(--nav-collapsed-bottom);
			height: var(--nav-bar-height);
			border-radius: var(--nav-collapsed-radius);
			background: rgb(0 0 0 / 0%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
			box-shadow: 0 5px 9px rgb(0 0 0 / 45%);
		}
		100% {
			left: var(--nav-collapsed-left);
			width: calc(var(--nav-collapsed-width) + var(--nav-expand-step) * 2);
			bottom: var(--nav-open-bottom);
			height: var(--nav-final-height);
			border-radius: var(--nav-expanded-radius);
			background: rgb(0 0 0 / 86%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
			box-shadow:
				0 12px 32px rgb(0 0 0 / 48%),
				inset 0 1px 0 rgb(255 255 255 / 10%);
		}
	}

	@keyframes fancy-nav-collapse-shell {
		0% {
			left: var(--nav-collapsed-left);
			width: calc(var(--nav-collapsed-width) + var(--nav-expand-step) * 2);
			bottom: var(--nav-open-bottom);
			height: var(--nav-final-height);
			border-radius: var(--nav-expanded-radius);
			background: rgb(0 0 0 / 86%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
			box-shadow:
				0 12px 32px rgb(0 0 0 / 48%),
				inset 0 1px 0 rgb(255 255 255 / 10%);
		}
		50% {
			left: var(--nav-collapsed-left);
			width: calc(var(--nav-collapsed-width) + var(--nav-expand-step) * 2);
			bottom: var(--nav-open-bottom);
			height: var(--nav-stage-one-height);
			border-radius: var(--nav-expanded-radius);
			background: rgb(0 0 0 / 60%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
			box-shadow:
				0 10px 25px rgb(0 0 0 / 48%),
				inset 0 1px 0 rgb(255 255 255 / 8%);
		}
		100% {
			left: var(--nav-collapsed-left);
			width: var(--nav-collapsed-width);
			bottom: var(--nav-collapsed-bottom);
			height: var(--nav-bar-height);
			border-radius: var(--nav-collapsed-radius);
			background: rgb(0 0 0 / 0%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
			box-shadow: 0 5px 9px rgb(0 0 0 / 45%);
		}
	}

	.fancy-nav-bar {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		max-width: 100%;
		height: var(--nav-bar-height);
		padding: 5px var(--nav-side-padding);
		background: rgb(0 0 0 / 60%);
		box-shadow: inset 0 -1px 0 rgb(255 255 255 / 15%);
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		border-radius: 30px;
		overflow: visible;
		pointer-events: auto;
		z-index: 4;
	}

	.fancy-mobile-nav:not(.is-modal-active) .fancy-nav-bar {
		background: rgb(0 0 0 / 60%);
		box-shadow: inset 0 -1px 0 rgb(255 255 255 / 15%);
		backdrop-filter: blur(4px) !important;
		-webkit-backdrop-filter: blur(4px) !important;
	}

	.fancy-mobile-nav.is-modal-active .fancy-nav-bar {
		left: 50%;
		right: auto;
		width: var(--nav-bar-frozen-width);
		max-width: var(--nav-bar-frozen-width);
		bottom: var(--nav-expand-step);
		background: transparent;
		box-shadow: none;
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		border-radius: var(--nav-collapsed-radius);
		transform: translateX(-50%);
	}

	.fancy-mobile-nav.is-modal-open .fancy-nav-bar {
		animation: fancy-nav-expand-bar 400ms cubic-bezier(0.22, 1, 0.36, 1) both;
	}

	.fancy-mobile-nav.is-modal-closing .fancy-nav-bar {
		animation: fancy-nav-collapse-bar 400ms cubic-bezier(0.22, 1, 0.36, 1) both;
	}

	@keyframes fancy-nav-expand-bar {
		0% {
			bottom: 0;
			background: rgb(0 0 0 / 60%);
			box-shadow: inset 0 -1px 0 rgb(255 255 255 / 15%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
		}
		100% {
			bottom: var(--nav-expand-step);
			background: transparent;
			box-shadow: none;
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
		}
	}

	@keyframes fancy-nav-collapse-bar {
		0%,
		50% {
			bottom: var(--nav-expand-step);
			background: rgb(0 0 0 / 20%);
			box-shadow: inset 0 -1px 0 rgb(255 255 255 / 6%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
		}
		100% {
			bottom: 0;
			background: rgb(0 0 0 / 60%);
			box-shadow: inset 0 -1px 0 rgb(255 255 255 / 15%);
			backdrop-filter: blur(4px);
			-webkit-backdrop-filter: blur(4px);
		}
	}

	.fancy-nav-modal-panel {
		position: absolute;
		left: 0;
		right: 0;
		top: 0;
		bottom: calc(var(--nav-bar-height) + var(--nav-expand-step));
		padding: 1.1rem 1rem 0.75rem;
		background: transparent;
		border-radius: 0;
		max-height: none;
		overflow: auto;
		opacity: 0;
		pointer-events: none;
		transform: translateY(0.5rem);
		transition:
			opacity 220ms ease,
			transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
		z-index: 2;
	}

	.fancy-nav-modal-panel.is-assistant {
		padding: 0;
		overflow: hidden;
	}

	.fancy-mobile-nav.is-modal-active .fancy-nav-modal-panel {
		opacity: 1;
		pointer-events: auto;
		transform: translateY(0);
	}

	.fancy-mobile-nav.is-modal-closing .fancy-nav-modal-panel {
		opacity: 0;
		pointer-events: none;
		transform: translateY(0.35rem);
	}

	.fancy-nav-modal-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 1rem;
	}

	.fancy-nav-modal-kicker {
		margin: 0;
		color: rgb(255 255 255 / 50%);
		font-size: 0.75rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.fancy-nav-modal-title {
		margin: 0.4rem 0 0;
		font-size: 1.9rem;
		line-height: 1.05;
	}

	.fancy-nav-modal-close {
		width: 2.5rem;
		height: 2.5rem;
		border: 0;
		border-radius: 999px;
		background: rgb(255 255 255 / 8%);
		color: #fff;
		font-size: 1.5rem;
		cursor: pointer;
	}

	.fancy-nav-modal-grid {
		display: grid;
		grid-template-columns: repeat(1, minmax(0, 1fr));
		gap: 1rem;
		margin-top: 0.85rem;
	}

	.fancy-nav-modal-card {
		min-height: 7rem;
		border-radius: 1.5rem;
		background: rgb(255 255 255 / 8%);
		padding: 0.5rem;
	}

	.fancy-nav-modal-card-large {
		grid-column: 1 / -1;
		min-height: 11rem;
	}

	.fancy-nav-assistant-close {
		position: absolute;
		top: max(0.75rem, env(safe-area-inset-top));
		right: 0.75rem;
		width: 2.5rem;
		height: 2.5rem;
		border: 0;
		border-radius: 999px;
		background: rgb(0 0 0 / 34%);
		color: #fff;
		font-size: 1.5rem;
		cursor: pointer;
		z-index: 7;
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
	}

	.fancy-nav-assistant-content {
		position: absolute;
		inset: 0;
	}

	.fancy-nav-assistant-content :global(.chat-window) {
		height: 100%;
	}

	.fancy-nav-assistant-content :global(.messages-container) {
		padding-top: 3.5rem;
	}

	.fancy-nav-assistant-content :global(.input-area) {
		padding-bottom: 0.75rem;
	}

	.fancy-nav-indicator {
		position: absolute;
		left: 5px;
		top: 5px;
		width: var(--nav-indicator-width);
		height: 50px;
		border-radius: 30px;
		transform: translateX(var(--nav-indicator-x));
		transition:
			transform 260ms cubic-bezier(0.22, 1, 0.36, 1),
			width 220ms ease;
		pointer-events: none;
		z-index: 1;
		opacity: 0;
	}

	.fancy-nav-indicator.is-visible {
		opacity: 1;
	}

	.fancy-nav-indicator::before {
		content: '';
		position: absolute;
		inset: 0;
		background: #000;
		border-radius: 30px;
	}

	.fancy-nav-indicator::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 30px;
		border: 1px solid transparent;
		background:
			linear-gradient(#000, #000) padding-box,
			linear-gradient(
					0deg,
					rgba(var(--active-nav-rgb), 0.58) 0%,
					rgba(var(--active-nav-rgb), 0) 100%
				)
				border-box;
	}

	.fancy-nav-item {
		position: relative;
		z-index: 3;
		display: grid;
		justify-items: center;
		align-content: center;
		width: var(--nav-indicator-width);
		min-width: var(--nav-indicator-width);
		height: 50px;
		margin-inline: calc((var(--nav-indicator-width) - var(--nav-icon-size)) / -2);
		padding: 0;
		border: 0;
		border-radius: 999px;
		background: transparent;
		color: rgba(255, 255, 255, 0.64);
		cursor: pointer;
		isolation: isolate;
		transition: color 160ms ease;
	}

	.fancy-nav-item::before {
		content: '';
		position: absolute;
		top: 0;
		left: 50%;
		width: 120px;
		height: 120px;
		border-radius: 50%;
		background: radial-gradient(
			50% 50% at 50% 50%,
			rgba(var(--item-color-rgb), 1) 0%,
			rgba(var(--item-color-rgb), 0) 100%
		);
		opacity: var(--item-active-opacity, 0);
		transform: translateX(-50%) scale(var(--item-active-scale, 0));
		transition:
			transform 840ms cubic-bezier(0.22, 1, 0.36, 1),
			opacity 720ms ease;
		pointer-events: none;
	}

	.nav-icon {
		font-size: var(--nav-icon-size);
		line-height: 1;
		color: rgb(255 255 255 / 70%);
		transition: color 160ms ease;
	}

	.fancy-nav-item.is-active {
		color: rgb(var(--item-color-rgb));
	}

	.fancy-nav-item.is-active .nav-icon,
	.fancy-nav-item.is-active .menu-item-icon {
		color: rgb(var(--item-color-rgb)) !important;
	}

	.fancy-nav-item-logo,
	.fancy-nav-item-more {
		pointer-events: auto;
	}

	.fancy-nav-logo {
		display: block;
		width: 28px;
		height: 28px;
		border-radius: 50%;
	}

	.fancy-nav-item-more :global(.menu-item.new-layout) {
		width: 100%;
		height: 100%;
		padding: 0;
		border: 0;
		color: inherit;
	}

	.fancy-nav-item-more :global(.menu-item.new-layout .menu-item-icon) {
		margin: 0;
		padding: 0;
		background: transparent;
		font-size: var(--nav-icon-size);
	}

	.fancy-nav-item-more :global(.menu-item.new-layout .new-layout-notification) {
		top: -5px;
		right: -8px;
	}

	.fancy-nav-divider {
		width: 1px;
		height: 18px;
		margin-inline: 2px;
		border-radius: 999px;
		background: rgb(255 255 255 / 16%);
		z-index: 3;
		pointer-events: none;
	}

	@media (min-width: 1124px) {
		.fancy-nav-backdrop {
			display: none;
		}

		.fancy-mobile-nav {
			left: 60px;
			right: auto;
			bottom: auto;
			top: 50%;
			width: calc(100vh - 3rem);
			max-width: calc(100vh - 3rem);
			transform: translate(-50%, -50%) rotate(90deg);
			transform-origin: center center;
			pointer-events: auto;
		}

		.fancy-mobile-nav.is-modal-active {
			transform: translate(-50%, -50%) rotate(90deg);
		}

		.fancy-mobile-nav.is-modal-active .fancy-nav-modal-panel {
			display: none;
		}

		.fancy-mobile-nav.is-nav-compact:not(.is-modal-active) {
			transform: translate(-50%, -50%) rotate(90deg) scale(0.7);
		}

		.fancy-mobile-nav.is-nav-compact:hover,
		.fancy-mobile-nav.is-nav-compact:focus-within {
			transform: translate(-50%, -50%) rotate(90deg) scale(1);
		}

		.fancy-nav-bar .nav-icon {
			transform: rotate(-90deg);
		}

		.fancy-nav-logo,
		.fancy-nav-item-more :global(.menu-item-icon) {
			transform: rotate(-90deg);
		}

		.fancy-nav-divider {
			margin-inline-start: auto;
			margin-inline-end: 0.5rem;
		}

		.fancy-nav-bar {
			position: absolute;
			left: 0;
			right: 0;
			bottom: 0;
			width: 100%;
			max-width: 100%;
			padding-left: 5px;
			padding-right: 5px;
			justify-content: flex-start;
			gap: 0.35rem;
		}

		.fancy-nav-item {
			margin-inline: 0;
		}
	}

	@media (min-width: 1124px) {
		.fancy-mobile-nav {
			width: calc(100vh - 4rem);
			max-width: calc(100vh - 4rem);
		}
	}

	@media (max-width: 1123px) {
		:global(.main-layout.new-layout .scroll.main-layout-scroll.new-layout) {
			padding-bottom: 0;
		}

		:global(.main-layout.new-layout .scroll-content-new-layout) {
			padding-bottom: calc(80px + 4vh + env(safe-area-inset-bottom, 0px)) !important;
		}

		:global(.main-layout.new-layout .dashboard-scroll .dashboard-content) {
			padding-bottom: calc(80px + 4vh + env(safe-area-inset-bottom, 0px)) !important;
		}
	}
</style>
